import Model, { attr, belongsTo } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export enum HomeawayLinkPaymentMethod {
  creditCard = 'credit_card',
  invoice = 'invoice',
}

export type HomeawayLinkStatus =
  | 'draft'
  | 'pushing'
  | 'pending_live'
  | 'live'
  | 'inactive'
  | 'failed';

export interface IHomeawayCheckpoint {
  name: string;
  status: 'true' | 'false';
}

// Mirrors `HomeawayLink::SUPPORTED_LOCATION_TYPES` in the API
export const HOMEAWAY_LOCATION_TYPES = [
  'LOCATION_TYPE_BEACH',
  'LOCATION_TYPE_BEACH_FRONT',
  'LOCATION_TYPE_BEACH_VIEW',
  'LOCATION_TYPE_DOWNTOWN',
  'LOCATION_TYPE_GOLF_COURSE_FRONT',
  'LOCATION_TYPE_GOLF_COURSE_VIEW',
  'LOCATION_TYPE_LAKE',
  'LOCATION_TYPE_LAKE_FRONT',
  'LOCATION_TYPE_LAKE_VIEW',
  'LOCATION_TYPE_MOUNTAIN',
  'LOCATION_TYPE_MOUNTAIN_VIEW',
  'LOCATION_TYPE_NEAR_OCEAN',
  'LOCATION_TYPE_OCEAN_FRONT',
  'LOCATION_TYPE_OCEAN_VIEW',
  'LOCATION_TYPE_RESORT',
  'LOCATION_TYPE_RIVER',
  'LOCATION_TYPE_RURAL',
  'LOCATION_TYPE_SKI_IN',
  'LOCATION_TYPE_SKI_OUT',
  'LOCATION_TYPE_TOWN',
  'LOCATION_TYPE_VILLAGE',
  'LOCATION_TYPE_WATER_VIEW',
  'LOCATION_TYPE_WATERFRONT',
];

export default abstract class HomeawayLinkModel extends Model {
  @attr('string') externalId!: string;
  @attr('string') headline!: string;
  @attr('string') description!: string;
  @attr('string') accountName!: string;
  @attr('boolean') listable!: boolean;
  @attr('string') paymentMethod!: HomeawayLinkPaymentMethod;
  @attr('number') minimumAge!: number;
  @attr('boolean', { defaultValue: true }) showLocation!: boolean;
  @attr('array', { defaultValue: () => [] }) locationTypes!: string[];
  @attr('number') commission!: number;
  @attr('boolean') commissionRefreshBookings!: boolean;
  @attr('date') newListingPromotionValidUntil!: Date | null;
  @attr('number') homeawayConnectionId!: number | null;
  @attr('string') status!: HomeawayLinkStatus | null;
  @attr('array', { defaultValue: () => [] })
  checkpoints!: IHomeawayCheckpoint[];

  @belongsTo('property', { inverse: 'homeawayLink', async: false })
  property!: ModelRegistry['property'];

  @cached
  get unfulfilledCheckpoints(): IHomeawayCheckpoint[] {
    return (this.checkpoints ?? []).filter(
      (checkpoint) => checkpoint.status === 'false',
    );
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'homeaway-link': HomeawayLinkModel;
  }
}
